import React, { useState, useEffect } from "react";
import { CallScriptManager } from "components/CallScriptManager";
import baseApi from "utils/axios-instance";
import { Loader2 } from "lucide-react";

interface DefaultQuestionsStepProps {
  onNext: (data: any) => void;
  initialData?: any;
  practiceData?: any;
}

export default function DefaultQuestionsStep({
  onNext,
  practiceData,
}: DefaultQuestionsStepProps) {
  const [teamId, setTeamId] = useState<string | null>(null);
  const [loadingTeamId, setLoadingTeamId] = useState(true);

  // Fetch teamId from current-user endpoint
  useEffect(() => {
    const fetchTeamId = async () => {
      try {
        const response = await baseApi.get("/dashboard/current-user", { withCredentials: true });
        const userData = response.data;
        setTeamId(userData.team_id);
      } catch (error: any) {
        console.error("Error fetching team ID:", error);
        // Fallback: try to get from practiceData if available
        if (practiceData?.team_id) {
          setTeamId(practiceData.team_id);
        }
      } finally {
        setLoadingTeamId(false);
      }
    };

    fetchTeamId();
  }, [practiceData]);

  const handleNext = (data: { call_script_id: string }) => {
    onNext({
      call_script_id: data.call_script_id || "",
      defaultQueries: [],
      customPrompt: "",
      autoSubmitEnabled: false,
    });
  };

  if (loadingTeamId) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-4" />
          <p className="text-gray-600">Loading...</p>
        </div>
      </div>
    );
  }

  if (!teamId) {
    return (
      <div className="max-w-4xl mx-auto">
        <div className="text-center">
          <p className="text-red-600">Unable to load team information. Please refresh the page.</p>
        </div>
      </div>
    );
  }

  return (
    <CallScriptManager
      mode="onboarding"
      teamId={teamId}
      specialty={practiceData?.specialty ?? null}
      onNext={handleNext}
    />
  );
}
